import React, { useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import {
  IconBuilding,
  IconInfoCircle,
  IconUsers,
  IconX,
  IconChevronUp,
  IconChevronDown,
  IconDatabase,
  IconUser,
  IconBox
} from '@tabler/icons-react'
import { DataboxItemData } from '../SampleTableData'
import { getFileIcon } from '../../Chatbot/utils/helpers'
import { isPersonalDatabox } from '../../Chatbot/utils/mcpServerUtils'
import { isLockedItem } from './lockUtils'

interface SelectedSourcesPanelProps {
  // Legacy fallback for callers passing only this one flag (gates the whole panel together)
  isFetchingDetails?: boolean
  // Per-section overrides so each category can reveal its content as soon as it's ready
  isFetchingPersonal?: boolean
  isFetchingTeam?: boolean
  isFetchingDataProducts?: boolean
  selectedRows: string[]
  selectedDocumentsByDatabox: { [databoxId: string]: { name: string; items: DataboxItemData[] } }
  databoxes: {
    id: string
    name: string
    databoxDocumentsCount?: number
    databoxDataProductsCount?: number
    filesCount?: number
    dataProductsCount?: number
  }[]
  databoxDetailsCache: { [key: string]: any }
  initialDataboxId?: string | null
  onRemoveDatabox?: (databoxId: string) => void
  onRemoveItem?: (databoxId: string, itemId: string) => void
  lockedDataboxIds?: string[]
  lockedItemIds?: string[]
  // Standalone data products (not tied to any databox)
  standaloneDataProducts?: Array<{ id: string; name: string }>
  onRemoveDataProduct?: (id: string) => void
  lockedDataProductIds?: string[]
  // Id of the entry in `databoxes` that is the user's Personal Databox (if any), so it can be
  // shown in its own "My Personal Databox" section, always before "Team Databoxes", regardless
  // of selection order.
  personalDataboxId?: string
}

const SelectedSourcesPanel: React.FC<SelectedSourcesPanelProps> = ({
  isFetchingDetails = false,
  isFetchingPersonal,
  isFetchingTeam,
  isFetchingDataProducts,
  selectedRows,
  selectedDocumentsByDatabox,
  databoxes,
  databoxDetailsCache,
  initialDataboxId,
  onRemoveDatabox,
  onRemoveItem,
  lockedDataboxIds = [],
  lockedItemIds = [],
  standaloneDataProducts = [],
  onRemoveDataProduct,
  lockedDataProductIds = [],
  personalDataboxId
}) => {
  const lockedDataboxIdSet = new Set(lockedDataboxIds.map(String))
  const lockedItemIdSet = new Set(lockedItemIds.map(String))
  const lockedDataProductIdSet = new Set(lockedDataProductIds.map(String))
  // In edit mode, only show the databox being edited
  let allDataboxIds = Array.from(
    new Set([...selectedRows, ...Object.keys(selectedDocumentsByDatabox)])
  )

  // Filter to show only the edited databox in edit mode
  if (initialDataboxId) {
    const smartDocsId = (databoxes.find((db) => String(db.id) === String(initialDataboxId)) as any)
      ?.smartDocsDataboxId

    const matchingIds = allDataboxIds.filter(
      (id) =>
        String(id) === String(initialDataboxId) ||
        (smartDocsId && String(id) === String(smartDocsId))
    )

    // Use ID with selections, or first match, or initialDataboxId
    allDataboxIds =
      matchingIds.length > 0
        ? [matchingIds.find((id) => selectedDocumentsByDatabox[id]) || matchingIds[0]]
        : [String(initialDataboxId)]
  }

  const hasAny = allDataboxIds.length > 0 || standaloneDataProducts.length > 0
  const [expandedDataboxes, setExpandedDataboxes] = useState<{ [id: string]: boolean }>({})

  // Resolve each section's own fetching state, falling back to the shared isFetchingDetails when
  // a caller doesn't provide a more specific flag (see prop comments above).
  const fetchingPersonal = isFetchingPersonal ?? isFetchingDetails
  const fetchingTeam = isFetchingTeam ?? isFetchingDetails
  const fetchingDataProducts = isFetchingDataProducts ?? isFetchingDetails
  const isAnyFetching = fetchingPersonal || fetchingTeam || fetchingDataProducts

  // Whether a given id (from allDataboxIds) refers to the Personal Databox - matched by either
  // `id` or `smartDocsDataboxId`, since callers may report either depending on what's available.
  const isPersonalId = (id: string) => {
    if (!personalDataboxId) return false
    if (String(id) === String(personalDataboxId)) return true
    const db = databoxes.find(
      (d) => String(d.id) === String(id) || String((d as any).smartDocsDataboxId) === String(id)
    )
    return (
      !!db &&
      (String(db.id) === String(personalDataboxId) ||
        String((db as any).smartDocsDataboxId) === String(personalDataboxId))
    )
  }

  // Split into "My Personal Databox" and "Team Databoxes" so they can be shown in that fixed
  // order regardless of selection order - skipped in edit mode (initialDataboxId), which only
  // ever shows the one Team databox being edited directly.
  const personalDataboxIds = initialDataboxId ? [] : allDataboxIds.filter(isPersonalId)
  const teamDataboxIds = initialDataboxId
    ? allDataboxIds
    : allDataboxIds.filter((id) => !isPersonalId(id))

  const isExpanded = (id: string) => expandedDataboxes[id] !== false // default expanded

  const toggleExpanded = (id: string) =>
    setExpandedDataboxes((prev) => ({ ...prev, [id]: !isExpanded(id) }))

  // Renders one databox card - shared by both the "My Personal Databox" and "Team Databoxes"
  // sections below.
  const renderDataboxCard = (databoxId: string) => {
    // Try to find databox by id or smartDocsDataboxId
    const databox = databoxes.find(
      (db) =>
        String(db.id) === String(databoxId) ||
        String((db as any).smartDocsDataboxId) === String(databoxId)
    )
    const databoxEntry = selectedDocumentsByDatabox[databoxId]
    const explicitSelection = databoxEntry?.items ?? null
    const databoxName = databoxEntry?.name ?? databox?.name
    const isSelected = selectedRows.includes(databoxId)
    const cache = databoxDetailsCache[databoxId]

    // Show chevron if there are custom selections
    const hasCustomSelection =
      explicitSelection !== null && explicitSelection !== undefined && explicitSelection.length > 0
    const isWholeDataboxSelected = !hasCustomSelection && isSelected

    // If databox is selected and user hasn't made a specific selection,
    // show all documents from the cache
    const rawFiles: any[] =
      explicitSelection !== null && explicitSelection !== undefined
        ? explicitSelection.filter((d) => d.type === 'file')
        : isSelected && cache
        ? cache.files || []
        : []
    const rawDataProducts: any[] =
      explicitSelection !== null && explicitSelection !== undefined
        ? explicitSelection.filter((d) => d.type === 'dataproduct')
        : isSelected && cache
        ? cache.dataProducts || []
        : []

    // De-duped by stable identity - the same file/product can appear under different id-space values.
    const dedupeByKey = <
      T extends {
        id?: string
        cognitiveSearchId?: string
        dataProductId?: string
        name?: string
      }
    >(
      list: T[]
    ): T[] => {
      const seen = new Set<string>()
      return list.filter((item) => {
        const key = String(
          item.cognitiveSearchId ?? item.dataProductId ?? item.id ?? item.name ?? ''
        )
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
    }
    const allFiles: any[] = dedupeByKey(rawFiles)
    const allDataProducts: any[] = dedupeByKey(rawDataProducts)

    // Always show total counts from databox
    const totalFilesCount =
      cache?.filesCount ??
      cache?.files?.length ??
      databox?.databoxDocumentsCount ??
      databox?.filesCount ??
      0
    const totalDataProductsCount =
      cache?.dataProductsCount ??
      cache?.dataProducts?.length ??
      databox?.databoxDataProductsCount ??
      databox?.dataProductsCount ??
      0
    const hasItems = totalFilesCount > 0 || totalDataProductsCount > 0
    const isPersonalDB = isPersonalDatabox(databoxId)

    return (
      <div
        key={databoxId}
        className="mb-2 border-t border-b theme-border-default rounded-lg overflow-hidden">
        {/* Databox header */}
        <div className="flex items-center py-[6px] px-2 theme-surface-muted border-b theme-border-default gap-1">
          <IconBox size={22} color="var(--team-agent)" className="text-info flex-shrink-0" />
          <div
            className="flex flex-col flex-1 min-w-0 cursor-pointer"
            onClick={() => toggleExpanded(databoxId)}>
            <span className="font-semibold text-base theme-text-heading truncate">
              {databoxName}
            </span>
            <div className="flex items-center gap-1">
              {isWholeDataboxSelected && (
                <span className="inline-flex items-center gap-1 theme-bg-text-subheading rounded px-1.5 py-0.5 text-xs text-white font-medium">
                  All items
                </span>
              )}
              {!isWholeDataboxSelected && (allFiles.length > 0 || allDataProducts.length > 0) && (
                <span className="inline-flex items-center gap-1 theme-bg-text-subheading rounded px-1.5 py-0.5 text-xs text-white font-medium">
                  {allFiles.length > 0 &&
                    `${allFiles.length} file${allFiles.length !== 1 ? 's' : ''}`}
                  {allFiles.length > 0 && allDataProducts.length > 0 && ', '}
                  {allDataProducts.length > 0 &&
                    `${allDataProducts.length} data product${
                      allDataProducts.length !== 1 ? 's' : ''
                    }`}
                  {' selected'}
                </span>
              )}
              <span className="text-sm theme-text-subheading">
                {hasItems && `${totalFilesCount} file${totalFilesCount !== 1 ? 's' : ''}`}
                {!isPersonalDB &&
                  hasItems &&
                  ` | ${totalDataProductsCount} data product${
                    totalDataProductsCount !== 1 ? 's' : ''
                  }`}
              </span>
            </div>
          </div>
          {/* Show chevron only if custom files are selected (not whole databox) */}
          {!isWholeDataboxSelected && hasItems && (
            <div
              className="bg-transparent border-none p-0 cursor-pointer text-theme-subheading hover:text-theme-heading"
              onClick={() => toggleExpanded(databoxId)}>
              {isExpanded(databoxId) ? <IconChevronUp size={20} /> : <IconChevronDown size={20} />}
            </div>
          )}
          {/* Show X icon only if entire databox is selected AND it isn't agent-locked */}
          {isWholeDataboxSelected &&
            onRemoveDatabox &&
            !lockedDataboxIdSet.has(String(databoxId)) && (
              <div
                className="bg-transparent border-none p-0 ml-1 cursor-pointer text-theme-subheading hover:text-danger"
                onClick={(e) => {
                  e.stopPropagation()
                  onRemoveDatabox(databoxId)
                }}>
                <IconX size={20} />
              </div>
            )}
        </div>

        {isExpanded(databoxId) && (
          <>
            {!isWholeDataboxSelected && allFiles.length > 0 && (
              <div>
                {allFiles.map((file, index) => (
                  <div
                    key={file.id || index}
                    className={`flex items-center py-[8px] pl-[2rem] !pr-[1rem] theme-hover-surface gap-1 ${
                      index < allFiles.length - 1 || allDataProducts.length > 0
                        ? 'border-b theme-border-default'
                        : ''
                    }`}>
                    {getFileIcon(file.name || file.fileName)}
                    <span className="text-xs theme-text-heading truncate flex-1">
                      {file.name || file.fileName || 'Unnamed file'}
                    </span>
                    {onRemoveItem && !isLockedItem(file, lockedItemIdSet) && (
                      <div
                        className="bg-transparent border-none p-0 flex-shrink-0 cursor-pointer theme-text-subheading hover:text-danger"
                        onClick={() => {
                          onRemoveItem(databoxId, file.id || String(index))
                        }}>
                        <IconX size={20} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Explicit selection: Data Products subsection */}
            {!isWholeDataboxSelected && allDataProducts.length > 0 && (
              <div>
                {allDataProducts.map((dp, index) => (
                  <div
                    key={dp.id || index}
                    className={`flex items-center py-[8px] pl-[2rem] !pr-[1rem] theme-hover-surface gap-1 ${
                      index < allDataProducts.length - 1 ? 'border-b theme-border-default' : ''
                    }`}>
                    <IconDatabase size={18} className="text-primary flex-shrink-0" />
                    <span className="text-xs theme-text-heading truncate flex-1">
                      {dp.name || dp.title || 'Unnamed product'}
                    </span>
                    {onRemoveItem && !isLockedItem(dp, lockedItemIdSet) && (
                      <button
                        className="bg-transparent border-none p-0 flex-shrink-0 cursor-pointer theme-text-subheading hover:text-danger"
                        onClick={() => {
                          onRemoveItem(databoxId, dp.id || String(index))
                        }}>
                        <IconX size={20} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {!isWholeDataboxSelected &&
              (allFiles.length > 0 || allDataProducts.length > 0) &&
              onRemoveDatabox &&
              !lockedDataboxIdSet.has(String(databoxId)) && (
                <div
                  className="flex justify-start py-1 pl-[2rem] !pr-[1rem] theme-text-brand-accent font-semibold bg-transparent border-t theme-border-default hover:underline cursor-pointer"
                  onClick={() => {
                    onRemoveDatabox(databoxId)
                  }}>
                  Remove all
                </div>
              )}
          </>
        )}
      </div>
    )
  }

  // Skeletons shaped like each section's eventual content (heading + card/row placeholders),
  // shown in place of a section that hasn't finished loading yet, so sections that ARE ready
  // render immediately and nothing jumps around once real data replaces the skeleton.
  const renderDataboxSectionSkeleton = (rows: number) => (
    <div className="mb-2">
      <div className="mb-1 flex items-center gap-1">
        <Skeleton circle height={16} width={16} className="animated-skeleton" />
        <Skeleton height={14} width={140} className="animated-skeleton" />
      </div>
      {Array.from({ length: rows }).map((_, index) => (
        <div
          key={index}
          className="mb-2 flex items-center gap-1 py-[6px] px-2 border-t border-b theme-border-default rounded-lg">
          <Skeleton circle height={22} width={22} className="animated-skeleton" />
          <div className="flex-1">
            <Skeleton height={14} width={160} className="animated-skeleton" />
            <Skeleton height={11} width={100} className="animated-skeleton mt-1" />
          </div>
        </div>
      ))}
    </div>
  )

  const renderDataProductsSectionSkeleton = (rows: number) => (
    <div className="mb-2">
      <div className="mb-1 mt-2 flex items-center gap-1">
        <Skeleton circle height={16} width={16} className="animated-skeleton" />
        <Skeleton height={14} width={120} className="animated-skeleton" />
      </div>
      <div className="border-t border-b theme-border-default rounded-lg overflow-hidden">
        {Array.from({ length: rows }).map((_, index) => (
          <div
            key={index}
            className={`flex items-center gap-1 py-[8px] px-2 ${
              index < rows - 1 ? 'border-b theme-border-default' : ''
            }`}>
            <Skeleton circle height={18} width={18} className="animated-skeleton" />
            <Skeleton height={14} width={180} className="animated-skeleton" />
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <div className="theme-surface overflow-hidden flex flex-col h-full">
      <div className="flex items-center justify-between">
        <div className="text-xl font-semibold theme-text-heading mb-[8px]">Selected sources</div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {!hasAny && !isAnyFetching && (
          <div className="flex flex-col h-full items-center justify-center py-auto my-auto theme-text-subheading text-center px-4">
            <IconInfoCircle size={50} className="mb-2 text-primary" stroke={1.5} />
            <h3 className="font-semibold theme-text-heading mb-1">No sources selected</h3>
            <p className="text-sm theme-text-subheading">
              Add files from your Personal Databox, pick a Team Databox, or choose specific
              documents and data products to use as sources.
            </p>
          </div>
        )}

        {(hasAny || isAnyFetching) && (
          <div>
            {fetchingPersonal
              ? renderDataboxSectionSkeleton(1)
              : personalDataboxIds.length > 0 && (
                  <>
                    <div className="mb-1 flex items-center gap-1">
                      <IconUser
                        size={16}
                        color="var(--color-secondary)"
                        className="text-info flex-shrink-0"
                      />
                      <h3 className="text-sm font-semibold theme-text-heading uppercase tracking-wide whitespace-nowrap !m-0">
                        My Personal Databox ({personalDataboxIds.length})
                      </h3>
                      <hr className="flex-1 ml-1 theme-border-default" />
                    </div>
                    {personalDataboxIds.map(renderDataboxCard)}
                  </>
                )}

            {fetchingTeam
              ? renderDataboxSectionSkeleton(2)
              : teamDataboxIds.length > 0 && (
                  <>
                    <div className="mb-1 flex items-center gap-1">
                      <IconUsers size={16} className="text-info flex-shrink-0" />
                      <h3 className="text-sm font-semibold theme-text-heading uppercase tracking-wide whitespace-nowrap !m-0">
                        {initialDataboxId
                          ? `Databoxes (${teamDataboxIds.length})`
                          : `Team Databoxes (${teamDataboxIds.length})`}
                      </h3>
                      <hr className="flex-1 ml-1 theme-border-default" />
                    </div>
                    {teamDataboxIds.map(renderDataboxCard)}
                  </>
                )}

            {fetchingDataProducts
              ? renderDataProductsSectionSkeleton(2)
              : standaloneDataProducts.length > 0 && (
                  <>
                    <div className="mb-1 mt-2 flex items-center gap-1">
                      <IconDatabase size={16} className="text-primary flex-shrink-0" />
                      <h3 className="text-sm font-semibold theme-text-heading uppercase tracking-wide whitespace-nowrap !m-0">
                        Data Products ({standaloneDataProducts.length})
                      </h3>
                      <hr className="flex-1 ml-1 theme-border-default" />
                    </div>
                    <div className="mb-2 border-t border-b theme-border-default rounded-lg overflow-hidden">
                      {standaloneDataProducts.map((product, index) => (
                        <div
                          key={product.id}
                          className={`flex items-center py-[8px] px-2 theme-hover-surface gap-1 ${
                            index < standaloneDataProducts.length - 1
                              ? 'border-b theme-border-default'
                              : ''
                          }`}>
                          <IconDatabase
                            size={18}
                            color="var(--team-agent)"
                            className="flex-shrink-0"
                          />
                          <span className="text-sm theme-text-heading truncate flex-1">
                            {product.name}
                          </span>
                          {onRemoveDataProduct &&
                            !lockedDataProductIdSet.has(String(product.id)) && (
                              <button
                                className="bg-transparent border-none p-0 flex-shrink-0 cursor-pointer theme-text-subheading hover:text-danger"
                                onClick={() => {
                                  onRemoveDataProduct(product.id)
                                }}>
                                <IconX size={20} />
                              </button>
                            )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
          </div>
        )}
      </div>
    </div>
  )
}

export default SelectedSourcesPanel
